import * as React from 'react';
import { cn } from '@/core/lib/utils';
import {
  Activity,
  Pill,
  Stethoscope,
  Heart,
  Brain,
  Thermometer,
  Droplet,
  Moon,
  Utensils,
  Zap,
  AlertCircle,
  FileText,
} from 'lucide-react';

type HealthLogType = 
  | 'symptom'
  | 'medication'
  | 'appointment'
  | 'vital'
  | 'mood'
  | 'temperature'
  | 'bloodwork'
  | 'sleep'
  | 'nutrition'
  | 'seizure'
  | 'incident'
  | 'note'
  | string;

interface HealthLogTypeIconProps {
  type: HealthLogType;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showBackground?: boolean;
}

const iconConfig: Record<string, { icon: React.ElementType; color: string; bgColor: string }> = {
  symptom: { icon: Activity, color: 'text-orange-600', bgColor: 'bg-orange-100' },
  medication: { icon: Pill, color: 'text-blue-600', bgColor: 'bg-blue-100' },
  appointment: { icon: Stethoscope, color: 'text-green-600', bgColor: 'bg-green-100' },
  vital: { icon: Heart, color: 'text-red-600', bgColor: 'bg-red-100' },
  mood: { icon: Brain, color: 'text-purple-600', bgColor: 'bg-purple-100' },
  temperature: { icon: Thermometer, color: 'text-amber-600', bgColor: 'bg-amber-100' },
  bloodwork: { icon: Droplet, color: 'text-rose-600', bgColor: 'bg-rose-100' },
  sleep: { icon: Moon, color: 'text-indigo-600', bgColor: 'bg-indigo-100' },
  nutrition: { icon: Utensils, color: 'text-lime-600', bgColor: 'bg-lime-100' },
  seizure: { icon: Zap, color: 'text-yellow-600', bgColor: 'bg-yellow-100' },
  incident: { icon: AlertCircle, color: 'text-red-600', bgColor: 'bg-red-100' },
  note: { icon: FileText, color: 'text-gray-600', bgColor: 'bg-gray-100' },
};

const sizeClasses = {
  sm: { icon: 'h-4 w-4', container: 'h-8 w-8' },
  md: { icon: 'h-5 w-5', container: 'h-10 w-10' },
  lg: { icon: 'h-6 w-6', container: 'h-12 w-12' },
};

export function HealthLogTypeIcon({
  type,
  size = 'md',
  className,
  showBackground = true,
}: HealthLogTypeIconProps) {
  const config = iconConfig[type.toLowerCase()] || iconConfig.note;
  const Icon = config.icon;

  if (showBackground) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-full',
          sizeClasses[size].container,
          config.bgColor,
          className
        )}
      >
        <Icon className={cn(sizeClasses[size].icon, config.color)} />
      </div>
    );
  }

  return <Icon className={cn(sizeClasses[size].icon, config.color, className)} />;
}

export default HealthLogTypeIcon;
